/* eslint-disable @typescript-eslint/no-explicit-any */
import { Virtual } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { useSnapshot } from "valtio";

import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { memoizedSelectedRewards } from "@components/OrderTaking/Store/feature";

import { kioskStore } from "@pages/Kiosk/store";

import { CategoryCard, LoyaltyCard } from "./LoyaltyCard";
import {
    RewardRowContainer,
    RewardSectionBlock,
    RewardSectionLabel,
    RewardsContainer,
    RewardsSectionsContainer,
    RewardTitle,
} from "./styledCss";

type rewardSwiperType = {
    rewardList: RewardItemType[];
    categoryData: Omit<loyaltyRewardType, "rewardList">;
};

type rewardSwiperListType = {
    rewards: loyaltyRewardType[];
    slidesPerView: number;
};

// one category row is roughly a card plus its breathing room
const SLIDE_HEIGHT_IN_REM = 19;
const SECTION_LABEL_HEIGHT_IN_REM = 3;
const SECTIONS_GAP_IN_REM = 1;
// the offers block takes the smaller share, it usually holds fewer categories
const OFFERS_HEIGHT_RATIO = 0.4;

export default function RewardSection(): ReactElement<any, any> | null {
    const { t } = useTranslation();
    const {
        project: {
            template: {
                pages: {
                    subSteps: {
                        customerBenefit: {
                            options: { rewards },
                        },
                    },
                },
            },
        },
        customerLanguage,
        isPrm,
    } = useSnapshot(kioskStore);

    return rewards.active ? (
        <RewardsContainer isPrm={isPrm}>
            <RewardTitle isPrm={isPrm}>
                {isPrm ? (
                    <strong>{t("Choose a reward")} </strong>
                ) : (
                    <strong>{rewards.languages[customerLanguage.name]} </strong>
                )}
            </RewardTitle>
            <RewardSwiperContainer />
        </RewardsContainer>
    ) : null;
}

function RewardSwiperContainer(): JSX.Element {
    const { t } = useTranslation();
    const { isPrm } = useSnapshot(kioskStore);
    const rewards = useSelector((state: any) => memoizedSelectedRewards(state));

    const offerRewards = rewards.filter(
        (reward: loyaltyRewardType) => reward.activeReward === true
    );
    const pointsRewards = rewards.filter(
        (reward: loyaltyRewardType) => reward.activeReward !== true
    );

    const availableHeightInRem = isPrm ? 22 : 63;

    // The PRM pane is too short to hold two scroll areas, so offers simply come
    // first there, in the single swiper.
    const isSplitInSections =
        !isPrm && offerRewards.length > 0 && pointsRewards.length > 0;

    if (!isSplitInSections) {
        return (
            <RewardsSectionsContainer isPrm={isPrm}>
                <RewardSwiperList
                    rewards={[...offerRewards, ...pointsRewards]}
                    slidesPerView={isPrm ? 1.2 : 3.3}
                />
            </RewardsSectionsContainer>
        );
    }

    const offersHeightInRem =
        (availableHeightInRem - SECTIONS_GAP_IN_REM) * OFFERS_HEIGHT_RATIO;
    const pointsHeightInRem =
        availableHeightInRem - SECTIONS_GAP_IN_REM - offersHeightInRem;

    const getSlidesPerView = (heightInRem: number): number =>
        Number(
            (
                (heightInRem - SECTION_LABEL_HEIGHT_IN_REM) /
                SLIDE_HEIGHT_IN_REM
            ).toFixed(2)
        );

    return (
        <RewardsSectionsContainer isPrm={isPrm}>
            <RewardSectionBlock
                isOfferSection={true}
                height={`${offersHeightInRem}rem`}
            >
                <RewardSectionLabel>{t("Offers")}</RewardSectionLabel>
                <RewardSwiperList
                    rewards={offerRewards}
                    slidesPerView={getSlidesPerView(offersHeightInRem)}
                />
            </RewardSectionBlock>
            <RewardSectionBlock
                isOfferSection={false}
                height={`${pointsHeightInRem}rem`}
            >
                <RewardSectionLabel>{t("With your points")}</RewardSectionLabel>
                <RewardSwiperList
                    rewards={pointsRewards}
                    slidesPerView={getSlidesPerView(pointsHeightInRem)}
                />
            </RewardSectionBlock>
        </RewardsSectionsContainer>
    );
}

function RewardSwiperList({
    rewards,
    slidesPerView,
}: rewardSwiperListType): JSX.Element {
    return (
        <Swiper
            /* Only the visible category rows stay mounted, which also keeps
               the horizontal swiper each of them holds out of the DOM until
               the row is actually reached. */
            modules={[Virtual]}
            virtual={{
                enabled: true,
                addSlidesBefore: 1,
                addSlidesAfter: 1,
            }}
            slidesPerView={slidesPerView}
            spaceBetween={5}
            direction={"vertical"}
            className="w-100"
            style={{
                flex: "1 1 0",
                minHeight: 0,
                padding: "0.5rem",
            }}
        >
            {rewards.map((reward: loyaltyRewardType, index: number) => {
                const { rewardList, ...categoryData } = reward;
                return (
                    <SwiperSlide key={index} virtualIndex={index}>
                        <RewardListContainer
                            rewardList={rewardList}
                            categoryData={categoryData}
                        />
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
}

function RewardListContainer({
    rewardList,
    categoryData,
}: rewardSwiperType): JSX.Element {
    return (
        <RewardRowContainer isOffer={categoryData.activeReward === true}>
            <CategoryCard categoryData={categoryData} />
            <Swiper
                slidesPerView={3.3}
                spaceBetween={10}
                direction={"horizontal"}
                className="position-absolute w-100"
                style={{
                    marginLeft: "15rem",
                    maxWidth: "45rem",
                }}
            >
                {rewardList.map((reward, index) => {
                    return (
                        <SwiperSlide key={index}>
                            <LoyaltyCard
                                isPromoCode={false}
                                couponData={reward}
                            />
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </RewardRowContainer>
    );
}
